import {data, layer, Map, MapMouseEvent, TargetedEvent} from "azure-maps-control";
import {LabelThematicStyler} from "@esWebAPI/es/map/styler/LabelThematicStyler";
import {Feature} from "geojson";
import {isPieClusteringOn} from "@esWebAPI/es/map/layer/azure/AzureLayer";
import type {MapLayer} from "@esWebAPI/es/map/layer/MapLayer";
import {debounce} from "lodash";

export class AzureMouseoverLabelHandler {

    private readonly _map: Map;
    private readonly _mapLayer: MapLayer;
    private readonly _azureLayer: layer.Layer;

    private _tooltip: HTMLDivElement;

    private _onMouseoverHandler: (e: MapMouseEvent) => void;
    private _updateLabelHandler: (e: MapMouseEvent) => void;
    private _resetMouseMoveHandler: () => void;

    private _debouncedGenerateLabel: ((e: MapMouseEvent | TargetedEvent, shape: Feature<data.Geometry, any>) => void) | null = null;

    /**
     * Setup mouseover on point/label layer.
     * @param map
     * @param mapLayer
     */
    public constructor(map: Map, mapLayer: MapLayer) {
        this._map = map;
        this._mapLayer = mapLayer;

        this._tooltip = document.getElementById("_es_tooltip") as HTMLDivElement;

        const azureLayerId = this._mapLayer.getActiveAzureLayerId();

        if (azureLayerId){
            this._azureLayer = this._map.layers.getLayerById(azureLayerId);
        } else {
            es.log.log("no getActiveAzureLayerId for layer. skipping check for mouseover labels", mapLayer);
        }

        this.init();
    }

    private async init() {
        const layerView = this._mapLayer.layerView;
        const layerInfo = this._mapLayer.layerInfo;

        const isMouseoverEnabled =
            !es.isNativeApp &&
            !es.isTerritoryComparePage && (
                layerView.styleSettings?.defaultStyle?.mouseover ||
                layerView.styleSettings?.defaultStyle?.symbolStyle?.mouseover
            );

        if (!isMouseoverEnabled) {
            es.log.log("setupMouseOverLabels : not enabled", layerView.title);
            return;
        }

        const labelThematicStyler = new LabelThematicStyler(
            layerView,
            layerInfo.attributes);

        let currentFeatureId: string | number | null = null;
        let currentLabelRequestId = 0;
        let pendingEvent: MapMouseEvent | TargetedEvent | null = null;

        this._resetMouseMoveHandler = () => {
            this._map.getCanvasContainer().style.cursor = 'default';
            this._tooltip.style.display = 'none';
            this._map.events.remove('mousemove', this._azureLayer, this._updateLabelHandler);
            currentFeatureId = null;
            currentLabelRequestId++;
            pendingEvent = null;

            if (this._debouncedGenerateLabel) {
                this._debouncedGenerateLabel.cancel();
            }
        };

        this._debouncedGenerateLabel = debounce((e: MapMouseEvent | TargetedEvent, shape: Feature<data.Geometry, any>) => {
            const requestId = ++currentLabelRequestId;
            const isCluster = !!shape.properties?.cluster;

            // grab current vals from lv each time, as doing it when the closure was created only copies the val as a primitive
            const isColorThematicEnabled = !!this._mapLayer.layerView.styleSettings?.colorThematicSettings?.enabled;
            const isPieClusteringEnabled = isPieClusteringOn(layerView);

            //es.log.log("_debouncedGenerateLabel :",
            //    {isPieClusteringEnabled, isCluster, isPieClusteringOn: isPieClusteringOn(layerView), lv: layerView.title, isColorThematicEnabled}
            //);
            if (isPieClusteringEnabled && isCluster && isColorThematicEnabled) {
                // this is a cluster so PieClusterLayer will show a chart for it
                return;
            }

            // only proceed if we still have a valid pending event
            if (!pendingEvent) {
                return;
            }

            labelThematicStyler.generateLabelForShapeProps(this._mapLayer, shape.properties).then((label) => {
                if (requestId !== currentLabelRequestId) {
                    return;
                }
                const pixel = pendingEvent.pixel;
                if (label && pixel) {
                    const t = this._tooltip;
                    t.innerHTML = label;
                    t.style.display = 'block';

                    const tooltipWidth = t.offsetWidth;
                    const mapWidthOffset = this._map.getMapContainer().offsetLeft;

                    t.style.left = mapWidthOffset + (pixel[0] - tooltipWidth / 2) + 2 + 'px';
                    t.style.top = pixel[1] + 16 + 'px';

                    t.style.textAlign = isCluster ? "left" : "center";
                }
            }).catch((err) => {
                es.log.warn("generateLabelForShapeProps err", err);
                if (requestId !== currentLabelRequestId) {
                    return;
                }
                this._resetMouseMoveHandler();
            });
        }, 300);

        this._updateLabelHandler = (e: MapMouseEvent|TargetedEvent) => {

            if (!this._mapLayer) {
                es.log.warn("_updateLabelHandler this._mapLayer does not exist");
                return;
            }

            if ((!e.shapes || e.shapes.length === 0) && !e.target) {
                this._resetMouseMoveHandler();
                return;
            }

            const shape = (e.target? e.target : e.shapes[0]) as Feature<data.Geometry, any>;
            const featureId = shape.id ?? shape.properties.id;
            const isCluster = !!shape.properties?.cluster;

            // store latest event for use in the debounced function
            pendingEvent = e;

            if (currentFeatureId === featureId) {
                // update position immediately for the same feature (no new request needed)
                const pixel = e.pixel;
                if (this._tooltip.style.display === 'block' && pixel) {
                    const tooltipWidth = this._tooltip.offsetWidth;
                    const mapWidthOffset = this._map.getMapContainer().offsetLeft;
                    this._tooltip.style.left = mapWidthOffset + (pixel[0] - tooltipWidth / 2) + 2 + 'px';
                    this._tooltip.style.top = pixel[1] + 16 + 'px';
                }
                return;
            }

            // cancel previous pending request if moving to a new feature
            if (this._debouncedGenerateLabel) {
                this._debouncedGenerateLabel.cancel();
            }

            currentFeatureId = featureId;

            // for clusters, use debouncing to avoid overwhelming server
            if (isCluster) {
                if (shape.properties.point_count > 1000) {
                    this._tooltip.innerHTML = '<span style="opacity: 0.7;">Loading...</span>';
                    this._tooltip.style.display = 'block';
                    const pixel = e.pixel;
                    if (pixel) {
                        const mapWidthOffset = this._map.getMapContainer().offsetLeft;
                        this._tooltip.style.left = mapWidthOffset + (pixel[0] - this._tooltip.offsetWidth / 2) + 2 + 'px';
                        this._tooltip.style.top = pixel[1] + 16 + 'px';
                        this._tooltip.style.textAlign = "left";
                    }
                }

                this._debouncedGenerateLabel(e, shape);
            } else {
                // For single points, generate label immediately (no debouncing)
                const requestId = ++currentLabelRequestId;

                labelThematicStyler.generateLabelForShapeProps(this._mapLayer, shape.properties).then((label) => {
                    if (requestId !== currentLabelRequestId) {
                        return;
                    }
                    const pixel = e.pixel;
                    if (label && pixel) {
                        const t = this._tooltip;
                        t.innerHTML = label;
                        t.style.display = 'block';

                        const tooltipWidth = t.offsetWidth;
                        const mapWidthOffset = this._map.getMapContainer().offsetLeft;

                        t.style.left = mapWidthOffset + (pixel[0] - tooltipWidth / 2) + 2 + 'px';
                        t.style.top = pixel[1] + 16 + 'px';

                        t.style.textAlign = "center";
                    }
                }).catch((err) => {
                    es.log.warn("generateLabelForShapeProps err", err);
                    if (requestId !== currentLabelRequestId) {
                        return;
                    }
                    this._resetMouseMoveHandler();
                });
            }
        };

        this._onMouseoverHandler = (e: MapMouseEvent) => {
            if (e.shapes?.length > 0) {
                this._map.getCanvasContainer().style.cursor = 'pointer';
                this._updateLabelHandler(e);
                this._map.events.add('mousemove', this._azureLayer, this._updateLabelHandler);
            }
        };

        this._map.events.add('mouseover', this._azureLayer, this._onMouseoverHandler);
        this._map.events.add('mouseout', this._azureLayer, this._resetMouseMoveHandler);
        this._map.events.add('mousedown', this._azureLayer, this._resetMouseMoveHandler);
    }

    public destroy(): void {
        if (this._azureLayer) {
            es.log.log("AzureMouseoverLabelHandler#dispose");

            if (this._debouncedGenerateLabel) {
                this._debouncedGenerateLabel.cancel();
                this._debouncedGenerateLabel = null;
            }

            this._map.events.remove('mouseover', this._azureLayer, this._onMouseoverHandler);
            this._map.events.remove('mouseout', this._azureLayer, this._resetMouseMoveHandler);
            this._map.events.remove('mousedown', this._azureLayer, this._resetMouseMoveHandler);
            this._map.events.remove('mousemove', this._azureLayer, this._updateLabelHandler);
        }
    }
}
